import { Button, Grid, GridItem, Stack, Text, useBreakpointValue, useDisclosure } from '@chakra-ui/react';
import { ServiceType, VariantType } from '@pelicargo/types';
import { Show } from '@pelicargo/ui';
import { estimatedTransitTimeOptions, modelUtils, prettyNumber, TimeZone } from '@pelicargo/utils';
import { FreightService, Provider } from '@prisma/client';
import { useMemo } from 'react';
import { HiEye, HiPlus } from 'react-icons/hi2';

import { trpc } from '../../config/trpc';
import { useCreateQuoteVariant } from '../../hooks/quoteVariant/useCreate';
import { useQuoteVariants } from '../../hooks/quoteVariant/useQuoteVariants';
import { useCurrentRequest } from '../../hooks/useCurrentRequest';
import { Quote, QuoteVariantByQuoteId } from '../../utils/types';
import { Accordion, AccordionBody, AccordionHeader } from '../Accordion';
import { AccordionButton } from '../AccordionButton';
import { BookingTemplate } from '../BookingTemplate';
import { FlightSegmentsTable } from '../FlightSegments/Table';
import { LabeledText } from '../LabeledText';
import { SimpleLabeledPopoverInput } from '../SimpleLabeledPopoverInput';
import { BacklogEditor } from './BacklogEditor';
import { InstructionTemplateMenu } from './QuoteCard';
import { QuoteVariantTable } from './QuoteVariantTable';

type Props = {
  quote: Quote & { provider?: Provider };
  isBookingOpen?: boolean;
};

export const QuoteCardBody = ({ quote, isBookingOpen = false }: Props) => {
  const [quoteVariants] = useQuoteVariants(quote?.id);

  const { data: fulfillmentPartner } = trpc.getFulfillmentPartner.useQuery(
    { id: quote?.fulfillment_partner_configuration_id },
    {
      enabled: !!quote?.fulfillment_partner_configuration_id,
      refetchOnWindowFocus: false,
    },
  );

  const isAirQuote = quote?.freight_service === FreightService.AFR;

  const { request, cargoList } = useCurrentRequest();
  const bookedQuoteVariantId = useMemo(
    () => quoteVariants.find((variant) => request.booked_quote_variant_id === variant.id)?.id,
    [quoteVariants, request.booked_quote_variant_id],
  );
  const hasSomeBookedQuoteVariantFromQuote = useMemo(() => !!bookedQuoteVariantId, [bookedQuoteVariantId]);
  const { isOpen: showAllQuoteVariants, onToggle: toggleShowAllQuoteVariants } = useDisclosure();

  const cwFlagged = modelUtils.cargos.mayImpactCw(cargoList);

  // TODO: this should be removed once the data model is updated
  const mapServiceTypeToVariantType = (serviceType: ServiceType) => {
    switch (serviceType) {
      case ServiceType.General:
        return VariantType.General;
      case ServiceType.Expedited:
        return VariantType.Expedited;
      case ServiceType.Urgent:
        return VariantType.Urgent;
      case ServiceType.Other:
        return VariantType.Other;
      default:
        return VariantType.General;
    }
  };

  // Ensure it's an array, even if undefined
  const serviceTypeArray = request?.service_type || [];

  // TODO: this is because service type is an array of strings instead of enums
  const convertedServiceType = serviceTypeArray.map(mapServiceTypeToVariantType as any);

  const { createDefault } = useCreateQuoteVariant(quote?.id, convertedServiceType?.[0] as VariantType);

  const originTimezone = quote?.origin_airport?.time_zone as TimeZone;
  const airportId = quote?.origin_airport_id || '';

  const handleQuoteVariantCreate = async () => {
    if (!quote?.id) return;
    await createDefault?.();
  };

  const renderQuoteVariant = (quoteVariant: QuoteVariantByQuoteId) => {
    const isBooked =
      quoteVariant?.id === request?.booked_quote_variant_id || quoteVariant?.id === request?.booked_quote_variant?.id;
    if (hasSomeBookedQuoteVariantFromQuote && !isBooked && !showAllQuoteVariants) return null;

    return (
      <GridItem key={quoteVariant?.id} colSpan={4}>
        <QuoteVariantTable quoteVariant={quoteVariant} quote={quote} isBooked={isBooked} />
      </GridItem>
    );
  };

  const templateColumns = useBreakpointValue({
    base: 'repeat(4, 1fr)',
    xxl: 'repeat(8, 1fr)',
  });

  const isExpired = useMemo(() => {
    const expiresAt = quote?.expires_at;
    if (!expiresAt) return false;

    const convertToTZ = (date: Date, timeZone: string) => {
      const newDate = new Date(date.toLocaleString('en-US', { timeZone }));
      return newDate;
    };
    const adjustedExpiresAt = convertToTZ(expiresAt, originTimezone);
    adjustedExpiresAt.setHours(23, 59, 59, 999);
    const now = convertToTZ(new Date(), originTimezone);

    return adjustedExpiresAt < now;
  }, [quote?.expires_at, originTimezone]);

  return (
    <Stack px="3" pb="3" spacing="0">
      <Stack direction="row" spacing="2" pt="4" align="center">
        <Show if={isAirQuote}>
          <SimpleLabeledPopoverInput
            name="expires_at"
            label="Expires At"
            inputType="date-picker"
            initialTimeZone={originTimezone}
            tabIndex={0}
            showTime={false}
            isHighlighted={isExpired}
            highlightColor="red.500"
            showClearButton
          />
        </Show>
        <SimpleLabeledPopoverInput
          name="estimated_transit_time"
          label="Est. Transit Time"
          inputType="select"
          tabIndex={0}
          options={estimatedTransitTimeOptions}
        />
        <SimpleLabeledPopoverInput
          name="chargeable_weight"
          label="Chargeable Weight (kg)"
          inputType="input"
          formatDisplayValue={prettyNumber}
          tabIndex={0}
          isHighlighted={cwFlagged}
          numberOptions={{ inputMode: 'numeric' }}
        />
        <Show if={!!fulfillmentPartner}>
          <LabeledText
            label="Fulfillment Partner"
            value={fulfillmentPartner?.account_as_fulfillment_partner?.name}
            textProps={{ noOfLines: 1 }}
          />
        </Show>
        <Show if={isAirQuote}>
          <BacklogEditor quote={quote} />
        </Show>
      </Stack>
      <Show if={!!quote}>
        <Stack direction="column" spacing={4} py={6}>
          <Grid templateColumns={templateColumns} gap={2}>
            {quoteVariants?.map(renderQuoteVariant)}
          </Grid>
          <Show if={isAirQuote}>
            <Stack direction="row" alignSelf="center">
              <Button variant="outline" size="sm" onClick={toggleShowAllQuoteVariants} leftIcon={<HiEye />}>
                {showAllQuoteVariants ? 'Hide unbooked quote variants' : 'Show unbooked quote variants'}
              </Button>
              <Button variant="outline" size="sm" onClick={handleQuoteVariantCreate} leftIcon={<HiPlus />}>
                Add Quote Variant
              </Button>
            </Stack>
          </Show>
        </Stack>
      </Show>
      <Show if={isAirQuote}>
        <FlightSegmentsTable quote={quote} />
      </Show>
      <Accordion isOpen={isBookingOpen}>
        <AccordionHeader>
          <Stack direction="row" justifyContent="space-between" alignItems="center" mt={4}>
            <Text variant="heading">Booking</Text>
            <Stack direction="row" alignItems="center" spacing={2}>
              <Show if={!!quote?.id}>
                <InstructionTemplateMenu quote={quote} airportId={airportId} />
              </Show>
              <AccordionButton />
            </Stack>
          </Stack>
        </AccordionHeader>
        <AccordionBody>
          <BookingTemplate mt={4} quote={quote} request={request} />
        </AccordionBody>
      </Accordion>
    </Stack>
  );
};
